import useAlerts from '@/composables/useAlerts';
import { computed } from 'vue';
import type { Market } from '@/common/predictionMarkets/types';
import type { LibMarket } from '@/common/typechain/src/contracts/FlmFunCore';

const alerts = useAlerts();

/**
 * Zero address constant (hardcoded to avoid importing ethers library)
 * Used for validation checks (e.g., invalid creator addresses)
 */
export const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';

/**
 * Centralized error logging utility for consistent error handling across the app
 *
 * **Benefits:**
 * - Single source of truth for error formatting
 * - Easy to extend with error tracking, filtering, or structured logging
 * - Consistent error messages across the entire codebase
 * - Red color formatting for easy identification in console
 *
 * **Usage example:**
 * ```typescript
 * import { logError } from '@/common/helpers';
 *
 * try {
 *   await fetchData();
 * } catch (error) {
 *   logError('MyComponent', 'fetching data', error);
 * }
 * ```
 *
 * @param component - Component or module name (e.g., 'HomePage', 'useCreatorMarkets')
 * @param context - Action or operation that failed (e.g., 'fetching creators', 'updating allowance')
 * @param error - The error object
 */
export function logError(component: string, context: string, error: unknown): void {
  console.error(`%c[${component}] Error ${context}:`, 'color: #ff0000; font-weight: bold;', error);
}

/**
 * Centralized warning logging utility for consistent warning messages across the app
 *
 * **Benefits:**
 * - Single source of truth for warning formatting
 * - Distinguishes warnings from errors
 * - Consistent warning messages across the entire codebase
 * - Orange/brown color formatting for easy identification in console
 *
 * **Usage example:**
 * ```typescript
 * import { logWarn } from '@/common/helpers';
 *
 * if (!isValid) {
 *   logWarn('MyComponent', 'Invalid data format', data);
 * }
 * ```
 *
 * @param component - Component or module name (e.g., 'HomePage', 'useCreatorMarkets')
 * @param message - Warning message describing the issue
 * @param details - Optional additional context or data
 */
export function logWarn(component: string, message: string, details?: unknown): void {
  if (details !== undefined) {
    console.warn(`%c[${component}] ${message}:`, 'color: #ff8c00; font-weight: bold;', details);
  } else {
    console.warn(`%c[${component}] ${message}`, 'color: #ff8c00; font-weight: bold;');
  }
}

/**
 * Zero hash constant (32-byte zero hash)
 * Replaces ethers.ZeroHash to avoid ethers import
 */
export const ZERO_HASH = '0x0000000000000000000000000000000000000000000000000000000000000000';

/**
 * Convert number or string to BigInt
 * Replaces ethers.toBigInt to avoid ethers import
 * @param value - Number or string to convert
 * @returns BigInt representation
 */
export function toBigInt(value: number | string | bigint): bigint {
  return BigInt(value);
}

/**
 * Validate Ethereum address format
 * Replaces ethers.isAddress to avoid ethers import
 * @param address - Address to validate
 * @returns True if valid Ethereum address format
 */
export function isAddress(address: string): boolean {
  return /^0x[0-9a-fA-F]{40}$/.test(address);
}

/**
 * Parse a decimal string into a BigInt with specified decimals
 * Replaces ethers.parseUnits to avoid ethers import
 * @param value - Decimal string (e.g., "1.5")
 * @param decimals - Number of decimals (e.g., 18 for tokens)
 * @returns BigInt representation
 * @example parseUnits("1.5", 18) // 1500000000000000000n
 */
export function parseUnits(value: string, decimals: number): bigint {
  const [integer, fraction = ''] = value.split('.');
  const paddedFraction = fraction.padEnd(decimals, '0').slice(0, decimals);
  return BigInt(integer + paddedFraction);
}

/**
 * Parse ether string into a BigInt (wei)
 * Replaces ethers.parseEther to avoid ethers import
 * @param value - Ether amount as string or number (e.g., "1.5")
 * @returns BigInt representation in wei (18 decimals)
 * @example parseEther("1.5") // 1500000000000000000n
 */
export function parseEther(value: string | number): bigint {
  return parseUnits(value.toString(), 18);
}

/**
 * Format a BigInt into a decimal string with specified decimals
 * Replaces ethers.formatUnits to avoid ethers import
 * @param value - BigInt value or string representation of BigInt
 * @param decimals - Number of decimals (e.g., 18 for tokens)
 * @returns Decimal string representation
 * @example formatUnits(1500000000000000000n, 18) // "1.5"
 */
export function formatUnits(value: bigint | string, decimals: number): string {
  const bigIntValue = typeof value === 'string' ? BigInt(value) : value;
  const str = bigIntValue.toString().padStart(decimals + 1, '0');
  const integerPart = str.slice(0, -decimals) || '0';
  const fractionalPart = str.slice(-decimals).replace(/0+$/, '');
  return fractionalPart ? `${integerPart}.${fractionalPart}` : integerPart;
}

/**
 * Convert address to checksummed format (EIP-55)
 * Normalizes to lowercase — sufficient to prevent ENS resolution attempts by ethers
 * and ensures consistent cache keys regardless of input casing.
 * @param address - Ethereum address (with or without 0x prefix)
 * @returns Lowercase 0x-prefixed address
 */
export function getAddress(address: string): string {
  return `0x${address.toLowerCase().replace(/^0x/, '')}`;
}

export const currentOrigin = computed(() => {
  return typeof window !== 'undefined' ? window.location.origin : '';
});

/**
 * Validate raw market data from contract to filter out invalid/placeholder markets
 *
 * **When to use:** Immediately after fetching raw market data from smart contract,
 * before conversion or blob fetching. Prevents processing uninitialized/corrupted data.
 *
 * **Invalid patterns detected:**
 * - Market ID 0 (placeholder/initialization value)
 * - Zero address creators (0x0000...0000)
 *
 * **Usage example:**
 * ```typescript
 * const rawMarket = await contract.getMarket(marketId);
 * if (!isValidRawMarket(rawMarket)) {
 *   logWarn('MyComponent', `Skipping invalid raw market: id='${rawMarket.id}', creator='${rawMarket.creator}'`);
 *   return;
 * }
 * ```
 *
 * @param rawMarket - Raw market struct from contract (LibMarket.MarketStructOutput)
 * @returns true if raw market is valid, false if should be skipped
 */
export function isValidRawMarket(rawMarket: LibMarket.MarketStructOutput): boolean {
  // Market ID 0 is often used as placeholder/initialization value
  if (!rawMarket.id || rawMarket.id === 0n) return false;
  // Zero address creators are invalid
  if (!rawMarket.creator || rawMarket.creator === ZERO_ADDRESS) return false;
  return true;
}

/**
 * Validate market data to filter out invalid/placeholder markets
 *
 * **When to use:** After converting raw market data to Market type, before caching
 * or displaying to user. Final validation layer for application-ready data.
 *
 * **Invalid patterns detected:**
 * - Market ID '0' (string format after conversion)
 * - Zero address creators (0x0000...0000)
 *
 * **Usage example:**
 * ```typescript
 * const market = await convertRawMarketToMarket(rawMarket);
 * if (!isValidMarket(market)) {
 *   logWarn('MyComponent', `Skipping invalid market: id='${market.id}', creator='${market.creatorId}'`);
 *   return;
 * }
 * ```
 *
 * @param market - Converted Market object ready for application use
 * @returns true if market is valid, false if should be skipped
 */
export function isValidMarket(market: Market): boolean {
  // Market ID 0 is often used as placeholder/initialization value
  if (!market.id || market.id === '0') return false;
  // Zero address creators are invalid
  if (!market.creatorId || market.creatorId === ZERO_ADDRESS) return false;
  return true;
}

export async function copyObsLinkToClipboard(creatorId: string, marketId?: string) {
  let url = `${currentOrigin.value}/studio/widget/${creatorId}/`;
  if (marketId) {
    url += `?marketId=${marketId}`;
  }
  await navigator.clipboard.writeText(url);
  alerts.add({ message: 'OBS Widget URL copied to clipboard!', classes: 'bg-blue-700' });
}

export async function copyToClipboard(text: string) {
  await navigator.clipboard.writeText(text);
  alerts.add({ message: 'Copied to clipboard!', classes: 'bg-blue-700' });
}

/**
 * Format a timestamp as a relative time string (e.g., "5m ago", "2h ago", "3d ago")
 * @param timestampMs - Timestamp in milliseconds
 * @param options - Optional configuration
 * @param options.includeWeeks - Whether to include weeks in the output (default: false)
 * @param options.switchToDateAfterDays - If specified, switches to full date after N days (e.g., "Jan 15, 2026")
 * @returns Relative time string or formatted date if past threshold
 */
export function formatRelativeTime(timestampMs: number, options: { includeWeeks?: boolean; switchToDateAfterDays?: number } = {}): string {
  if (timestampMs === 0) return 'No activity';

  const now = Date.now();
  const diffMs = now - timestampMs;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return 'just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else if (options.switchToDateAfterDays && diffDays >= options.switchToDateAfterDays) {
    // Switch to full date format
    const date = new Date(timestampMs);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } else if (options.includeWeeks) {
    const diffWeeks = Math.floor(diffDays / 7);
    if (diffWeeks < 4) {
      return `${diffWeeks}w ago`;
    }
  }

  // Continue with months/years
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) {
    return `${diffMonths}mo ago`;
  } else {
    const diffYears = Math.floor(diffMonths / 12);
    return `${diffYears}y ago`;
  }
}

/**
 * Format a timestamp/Date as a full date and time string for tooltips
 * @param input - Timestamp in milliseconds or Date object
 * @param options - Optional formatting options
 * @param options.includeSeconds - Whether to include seconds (default: true)
 * @param options.hour12 - Whether to use 12-hour format (default: false)
 * @returns Formatted date string
 */
export function formatFullDateTime(input: number | Date, options: { includeSeconds?: boolean; hour12?: boolean } = {}): string {
  const { includeSeconds = true, hour12 = false } = options;
  const date = typeof input === 'number' ? new Date(input) : input;

  const formatOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: hour12 ? 'numeric' : '2-digit',
    minute: '2-digit',
    hour12,
  };

  if (includeSeconds) {
    formatOptions.second = '2-digit';
  }

  return date.toLocaleString('en-US', formatOptions);
}
